import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavBar } from "../../../../Contexts/navbar.items.context.jsx";

const safeParse = (v, fallback) => {
  try {
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

const RESET_WINDOW_POSITION_EVENT = "FBX_RESET_WINDOW_POSITION";

const DragBox2026 = ({
  storageKey = "DRAGBOX_2026_POS",
  defaultPos = { x: 80, y: 260 },

  // header
  title = "DragBox",
  countText = "",
  wideTitle = false,

  // buttons
  showReset = false,
  onReset, // optional callback
  showClose = true,
  closeValue = null, // what to set activeSideButton to when closing (default: null)
  closeButtonText = "X",
  onClose, // optional override; if provided, runs instead of context close

  // sizing / style
  minWidth = 700,
  maxWidth = 700,
  zIndex = 500,
  className = "card shadow-sm",
  style = {},

  // content
  children,
}) => {
  const { updateActiveSideButton, activeSideButton } = useNavBar();

  const initialPos = useMemo(() => {
    const saved = safeParse(localStorage.getItem(storageKey), null);
    return saved && typeof saved.x === "number" && typeof saved.y === "number" ? saved : defaultPos;
  }, [storageKey, defaultPos]);

  const [position, setPosition] = useState(initialPos);
  const [dragging, setDragging] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    setDragging(true);
    dragOffsetRef.current = { x: e.clientX - position.x, y: e.clientY - position.y };
  };

  const onMouseMove = (e) => {
    if (!dragging) return;
    const next = {
      x: e.clientX - dragOffsetRef.current.x,
      y: e.clientY - dragOffsetRef.current.y,
    };
    setPosition(next);
  };

  const onMouseUp = (e) => {
    if (!dragging) return;
    setDragging(false);

    const finalPos = {
      x: e.clientX - dragOffsetRef.current.x,
      y: e.clientY - dragOffsetRef.current.y,
    };

    setPosition(finalPos);
    try {
      localStorage.setItem(storageKey, JSON.stringify(finalPos));
    } catch {}
  };

  useEffect(() => {
    if (!dragging) return;

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  useEffect(() => {
    const handleExternalReset = (event) => {
      const key = event?.detail?.storageKey || "";
      if (key !== storageKey && key !== "*") return;

      try {
        localStorage.removeItem(storageKey);
      } catch {}

      setPosition(defaultPos);
      if (typeof onReset === "function") onReset();
    };

    window.addEventListener(RESET_WINDOW_POSITION_EVENT, handleExternalReset);
    return () => window.removeEventListener(RESET_WINDOW_POSITION_EVENT, handleExternalReset);
  }, [storageKey, defaultPos, onReset]);

  const handleReset = (e) => {
    e?.stopPropagation?.();

    try {
      localStorage.removeItem(storageKey);
    } catch {}

    // ✅ reset to default positions
    setPosition(defaultPos);

    if (typeof onReset === "function") onReset();
  };

  const handleClose = (e) => {
    e?.stopPropagation?.();

    // allow consumer to override close behavior
    if (typeof onClose === "function") {
      onClose({ activeSideButton });
      return;
    }

    // default close behavior = close the active side button view
    updateActiveSideButton(closeValue);
  };

  return (
    <div
      style={{
        position: "absolute",
        left: position.x,
        top: position.y,
        zIndex,
        minWidth,
        maxWidth,
        border: "2px solid navy",
        borderRadius: 8,
        backgroundColor: "var(--bs-body-bg)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        ...style,
      }}
      className={className}
    >
      {/* Header = drag handle */}
      <div
        className="card-header py-2 d-flex align-items-center"
        onMouseDown={onMouseDown}
        style={{ cursor: "grab", userSelect: "none" }}
        title="Drag"
      >
        {/* LEFT: Drag label + Reset button */}
        <div className={`d-flex align-items-center gap-2 ${wideTitle ? "flex-shrink-0" : "col-16"}`}>
          {showReset && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleReset}
              title="Reset Position"
            >
              Reset
            </button>
          )}
        </div>

        {/* CENTER: title */}
        <strong
          className="text-center flex-grow-1 text-primary"
          style={wideTitle ? { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } : undefined}
          title={typeof title === "string" ? title : undefined}
        >
          {title}
          {!!countText && <span className="fw-normal fs-7 ms-1">{countText}</span>}
        </strong>

        {/* RIGHT: close */}
        <div className={`ms-auto d-flex gap-1 justify-content-end ${wideTitle ? "flex-shrink-0" : "col-16"}`}>
          {showClose && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={handleClose}
              title={closeButtonText === "X" ? "Close" : closeButtonText}
              style={{ whiteSpace: "nowrap" }}
            >
              {closeButtonText}
            </button>
          )}
        </div>
      </div>

      <div className="card-body p-2">{children}</div>
    </div>
  );
};

export default DragBox2026;
